"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSelectedSensor = void 0;
const react_1 = require("react");
const STORAGE_KEY_PREFIX = 'hg_selected_sensor_';
/**
 * Manages the currently selected sensor within a category's detail screen.
 * Persists selection to localStorage (web) or falls back to in-memory state.
 *
 * @param category - Category key (e.g., 'water', 'hvac', 'air', 'electrical')
 * @param sensorCount - Total number of sensors in this category (used for bounds checking)
 */
const useSelectedSensor = (category, sensorCount, 
/**
 * Sensor ids in list order. When supplied the selection is ALSO persisted by
 * id and restored from it, so a list whose length or order changes — a hub
 * gaining a linked gateway's sensors, say — keeps pointing at the same
 * physical device instead of silently sliding to a different one.
 */
sensorIds) => {
    const storageKey = `${STORAGE_KEY_PREFIX}${category}`;
    const idStorageKey = `${STORAGE_KEY_PREFIX}id_${category}`;
    const [selectedIndex, setSelectedIndex] = (0, react_1.useState)(() => {
        try {
            const stored = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            const parsed = stored ? parseInt(stored, 10) : 0;
            return isNaN(parsed) ? 0 : parsed;
        }
        catch {
            return 0;
        }
    });
    // Restore by id first — it survives list growth/reordering, unlike the index.
    const sensorIdKey = sensorIds?.join('|') ?? '';
    (0, react_1.useEffect)(() => {
        if (!sensorIds || sensorIds.length === 0)
            return;
        try {
            const storedId = typeof window !== 'undefined' ? localStorage.getItem(idStorageKey) : null;
            if (!storedId)
                return;
            const idx = sensorIds.indexOf(storedId);
            if (idx >= 0 && idx !== selectedIndex) {
                setSelectedIndex(idx);
                localStorage.setItem(storageKey, String(idx));
            }
        }
        catch {
            // Ignore storage errors
        }
        // selectedIndex intentionally omitted: this restores a stored selection,
        // it must not fight the user's own subsequent selection.
    }, [sensorIdKey, idStorageKey, storageKey]);
    // Bounds check: if sensors removed and index is out of bounds, reset
    (0, react_1.useEffect)(() => {
        if (sensorCount > 0 && selectedIndex >= sensorCount) {
            setSelectedIndex(0);
            try {
                localStorage.setItem(storageKey, '0');
            }
            catch {
                // Ignore storage errors
            }
        }
    }, [sensorCount, selectedIndex, storageKey]);
    const selectSensor = (0, react_1.useCallback)((index) => {
        if (index >= 0 && index < sensorCount) {
            setSelectedIndex(index);
            try {
                localStorage.setItem(storageKey, String(index));
                const id = sensorIds?.[index];
                if (id)
                    localStorage.setItem(idStorageKey, id);
            }
            catch {
                // Ignore storage errors
            }
        }
    }, [sensorCount, storageKey, idStorageKey, sensorIds]);
    const selectNext = (0, react_1.useCallback)(() => {
        if (sensorCount <= 1)
            return;
        const next = (selectedIndex + 1) % sensorCount;
        selectSensor(next);
    }, [selectedIndex, sensorCount, selectSensor]);
    const selectPrevious = (0, react_1.useCallback)(() => {
        if (sensorCount <= 1)
            return;
        const prev = (selectedIndex - 1 + sensorCount) % sensorCount;
        selectSensor(prev);
    }, [selectedIndex, sensorCount, selectSensor]);
    const selectById = (0, react_1.useCallback)((sensorId, sensors) => {
        const idx = sensors.findIndex((s) => s.sensorId === sensorId);
        if (idx >= 0)
            selectSensor(idx);
    }, [selectSensor]);
    return {
        selectedIndex: sensorCount > 0 ? Math.min(selectedIndex, sensorCount - 1) : 0,
        selectSensor,
        selectNext,
        selectPrevious,
        selectById,
    };
};
exports.useSelectedSensor = useSelectedSensor;
